import bugsnag from "@bugsnag/js";
import classNames from "classnames";
import every from "lodash/every";
import map from "lodash/map";
import noop from "lodash/noop";
import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import Button from "../../../../components/atoms/Button";
import useCustomizationContext from "../../../../hooks/useCustomizationContext";
import { ReactComponent as IconCheck } from "../../../../icons/check.svg";
import { ReactComponent as IconSave } from "../../../../icons/save.svg";
import { ReactComponent as IconCart } from "../../../../icons/shopping-bag.svg";
import { Content } from "../../../../types/customization/content";
import { SelectionPhoto } from "../../../../types/selection/selection";
import { KustomTrackEvents } from "../../../../types/track";
import { buildCallbackInput, countPages } from "../../../../utils/customization/configuration";
import { getEmptyPageKeys } from "../../../../utils/customization/content";
import {
  getCoverFilteredPages,
  getNoCoverDefinitions,
} from "../../../../utils/customization/cover";
import {
  findCroppedPictures,
  findPageAndDefinition,
} from "../../../../utils/customization/finders";
import { getPagePosition, getPageTitle } from "../../../../utils/customization/page";
import { getMaxPageReachedMessage } from "../../../../utils/customMessages";
import { convertSelectionPhotoToContent } from "../../../../utils/selection/photo";
import ConfirmModal from "../../../molecules/ConfirmModal";
import CharacteristicsSummary from "../../../molecules/customization/CharacteristicsSummary";
import TranslationByType from "../../../molecules/customization/TranslationByType";
import OptionChoiceModal from "../../../molecules/modals/OptionChoiceModal";
import QuitCustomizationModal from "../../../molecules/modals/QuitCustomizationModal";
import RemainingActionsModal from "../../../molecules/modals/RemainingActionsModal";
import "./style.scss";

export type CustomizationOnQuit = () => void;
export type CustomizationOnSave = (content: Content) => Promise<Content>;
export type CustomizationTrack = (name: string) => Promise<void>;

interface Props {
  onQuit?: CustomizationOnQuit;
  onSave?: CustomizationOnSave;
}

const CustomizationHeader: FunctionComponent<Props> = ({ onQuit, onSave }) => {
  const {
    content,
    configuration,
    dispatch,
    selection: { photos: selectionPhotos },
    template,
    track,
    ui: { changesToSaveCount, pagesConfigMapping },
  } = useCustomizationContext();
  const intl = useIntl();
  const maxPages = configuration?.pages?.max;
  const noCoverPageDefinitions = getNoCoverDefinitions(template.pageDefinitions);
  const hasMoreThanMaxPages = !!maxPages && countPages(content, noCoverPageDefinitions) > maxPages;
  const maxPagesMessage = getMaxPageReachedMessage(content.templateType, configuration);

  const canQuitWithoutLoss =
    !changesToSaveCount && every(selectionPhotos, ({ fotomKey }) => fotomKey);
  const onQuitConfirmationMessage = intl.formatMessage({
    id: "kustom_quit_confirmation",
  });

  const callbackInput = useMemo(
    () => buildCallbackInput(content, noCoverPageDefinitions),
    [content, noCoverPageDefinitions]
  );

  const doSave:
    | ((
        newContent: Content,
        newSelectionPhotos: SelectionPhoto[],
        showConfirmNotification?: boolean
      ) => Promise<boolean>)
    | undefined = useMemo(() => {
    if (!onSave) {
      return;
    }

    return (
      newContent: Content,
      newSelectionPhotos: SelectionPhoto[],
      showConfirmNotification = true
    ) => {
      if (!every(newSelectionPhotos, ({ fotomKey }) => fotomKey)) {
        dispatch({
          code: "KFE-002",
          type: "addNotificationByCode",
        });

        return Promise.resolve(false);
      }

      if (hasMoreThanMaxPages) {
        dispatch({
          code: "KFE-003",
          customMessage: maxPagesMessage,
          type: "addNotificationByCode",
        });

        return Promise.resolve(false);
      }

      const contentToSave: Content = {
        ...newContent,
        selectionPhotos: map(newSelectionPhotos, convertSelectionPhotoToContent),
      };

      return onSave(contentToSave)
        .then((contentSaved: Content) => {
          dispatch({
            content: contentSaved,
            type: "setSavedContent",
          });
          if (showConfirmNotification) {
            dispatch({
              code: "KFV-001",
              type: "addNotificationByCode",
            });
          }

          return true;
        })
        .catch(() => {
          dispatch({
            code: "KFE-001",
            type: "addNotificationByCode",
          });

          return false;
        });
    };
  }, [dispatch, hasMoreThanMaxPages, onSave, maxPagesMessage]);

  const [displayQuitCustomizationModal, setDisplayQuitCustomizationModal] =
    useState<boolean>(false);

  const onQuitWithTracking = () => {
    if (onQuit) {
      track(KustomTrackEvents.QUIT_CLICKED).finally(() => onQuit());
    }
  };

  const saveAndQuit = (): Promise<void> => {
    if (doSave && !hasMoreThanMaxPages) {
      return doSave(content, selectionPhotos)
        .then(() => {
          onQuitWithTracking();
        })
        .catch((e: Error) => {
          bugsnag.notify(e);
          // todo define action if needed
        });
    }
    return Promise.resolve();
  };

  const handleConfirmQuitCustomizationModal = () => {
    if (configuration?.isGuest && configuration?.onSave) {
      configuration?.onSave(saveAndQuit);
    } else {
      saveAndQuit().catch(() => {
        dispatch({
          code: "KFE-001",
          type: "addNotificationByCode",
        });
      });
    }
    setDisplayQuitCustomizationModal(false);
  };

  const hideQuitCustomizationModal = () => {
    onQuitWithTracking();
  };

  const quitElement = (
    <Button
      onClick={() => setDisplayQuitCustomizationModal(true)}
      title={intl.formatMessage({ id: "kustom_quit" })}
      type="error-lightest"
    >
      {intl.formatMessage({ id: "kustom_quit" })}
    </Button>
  );

  const [saveRequested, setSaveRequested] = useState<boolean>(false);
  const [saveDone, setSaveDone] = useState<boolean>(false);
  let saveElement;
  if (doSave) {
    const save = (): Promise<void> => {
      setSaveRequested(true);
      return doSave(content, selectionPhotos, false)
        .then(() => {
          setTimeout(() => setSaveDone(true), 800);
          setTimeout(() => {
            setSaveRequested(false);
            setSaveDone(false);
          }, 3000);
        })
        .catch((e: Error) => {
          bugsnag.notify(e);
          // todo define action if needed
        });
    };

    const onSaveClick = () => {
      if (configuration?.isGuest && configuration?.onSave) {
        configuration?.onSave(save);
      } else {
        save().catch((e: Error) => {
          bugsnag.notify(e);
          // todo define action if needed
        });
      }
    };

    const isSaveActive = !hasMoreThanMaxPages;

    saveElement = (
      <div
        className={classNames("CustomizationHeader-save", {
          "CustomizationHeader-save--clicked": saveRequested,
          "CustomizationHeader-save--success": saveDone,
        })}
      >
        <Button
          disabled={!isSaveActive || saveRequested || saveDone}
          onClick={onSaveClick}
          title={intl.formatMessage({ id: "kustom_save" })}
          type="primary-inverted"
        >
          <span className="CustomizationHeader-save-button">
            <IconSave />{" "}
            <span className="CustomizationHeader-save-button-text">
              {intl.formatMessage({ id: "kustom_save" })}
            </span>
          </span>
          <span className="CustomizationHeader-save-done">
            <IconCheck />
            <span className="CustomizationHeader-save-done-text">
              {intl.formatMessage({ id: "kustom_CustomizationHeader_saved" })}
            </span>
          </span>
        </Button>
      </div>
    );
  }

  const emptyPages = getEmptyPageKeys(content.pages, template.pageDefinitions);
  const noCoverPages = getCoverFilteredPages(content.pages, noCoverPageDefinitions);
  const emptyPagesNames = emptyPages.slice(0, 4).map((pageKey) => {
    const { pageDefinition } = findPageAndDefinition(pageKey, template, content);
    if (pageDefinition.type === "front-cover") {
      return intl.formatMessage({ id: "kustom_CoverPanel_cover" });
    }
    const pagePosition = getPagePosition(noCoverPages, pageKey);
    const pageTitle: string | null = getPageTitle(
      pageKey,
      pagesConfigMapping,
      content.locale,
      configuration,
      template.templateType,
      intl,
      pagePosition
    );

    return pageTitle || `${pagePosition}`;
  });

  const [displayRemainingActionsModal, setDisplayRemainingActionsModal] = useState<boolean>(false);
  const [hasSeenCroppedWarning, setHasSeenCroppedWarning] = useState<boolean>(false);
  const hideRemainingActionsModal = () => {
    setDisplayRemainingActionsModal(false);
    setHasSeenCroppedWarning(true);
  };
  const handleConfirmRemainingActionsModal = () => {
    hideRemainingActionsModal();
    setHasSeenCroppedWarning(true);

    if (hasOptionToChoose) {
      setDisplayOptionChoiceModal(true);
    } else {
      doFinalizeWithTracking();
    }
  };

  const [displayMissingPagesModal, setDisplayMissingPagesModal] = useState<boolean>(false);
  const handleDismissMissingPagesModal = useCallback(() => {
    setDisplayMissingPagesModal(false);
  }, [setDisplayMissingPagesModal]);

  const [hasOptionToChoose, setHasOptionToChoose] = useState<boolean>(
    template.templateType === "book" && !!configuration?.options?.length
  );
  const [displayOptionChoiceModal, setDisplayOptionChoiceModal] = useState<boolean>(false);

  const hideOptionChoiceModal = () => {
    setHasOptionToChoose(false);
    setDisplayOptionChoiceModal(false);
  };
  const handleConfirmOptionChoiceModal = () => {
    hideOptionChoiceModal();
    doFinalizeWithTracking();
  };

  let finalizeElement;
  let doFinalize = noop;
  const finalize = configuration?.finalize;
  const doFinalizeWithTracking = () => {
    track(KustomTrackEvents.FINALIZE_CLICKED).finally(() => doFinalize());
  };
  if (finalize) {
    doFinalize = () => {
      finalize.onFinalize(callbackInput).catch(() => {
        dispatch({
          code: "KFE-001",
          type: "addNotificationByCode",
        });
      });
    };

    const isFinalizeActive = finalize.isActive(callbackInput);
    const onFinalizeClick = () => {
      const doSaveOrNot = doSave ? doSave(content, selectionPhotos) : Promise.resolve(true);
      doSaveOrNot
        .then((saved) => {
          if (!saved) {
            return;
          }

          if (
            emptyPages.length > 0 ||
            (hasSeenCroppedWarning === false && findCroppedPictures(content.pages).length === 0)
          ) {
            setDisplayRemainingActionsModal(true);
          } else if (hasOptionToChoose) {
            setDisplayOptionChoiceModal(true);
          } else {
            doFinalizeWithTracking();
          }
        })
        .catch((e: Error) => {
          bugsnag.notify(e);
          // todo define action if needed
        });
    };

    finalizeElement = (
      <span
        className="CustomizationHeader-finalize"
        onClick={() => {
          // ⚠️ if isFinalize condition change, check if modal is still ok
          if (!isFinalizeActive) {
            setDisplayMissingPagesModal(true);
          }
        }}
      >
        <Button
          type="primary"
          disabled={!isFinalizeActive}
          onClick={() => {
            onFinalizeClick();
          }}
        >
          <IconCart />
          {finalize.label}
        </Button>
      </span>
    );
  }

  // temporarily hidden
  // let helpElement;
  // const [displayHelp, setDisplayHelp] = useState<boolean>(false);
  // if (template.templateType === "book") {
  //   const onHelpClickHandler = () => {
  //     // show carousel
  //     setDisplayHelp(true);
  //   };

  //   helpElement = (
  //     <>
  //       <button
  //         className="CustomizationHeader-button"
  //         onClick={onHelpClickHandler}
  //         title={intl.formatMessage({ id: "kustom_quit" })}
  //         type="button"
  //       >
  //         <IconHelp />
  //       </button>
  //       <LottieCarousel display={displayHelp} onDismiss={() => setDisplayHelp(false)} />
  //     </>
  //   );
  // }

  useEffect(() => {
    if (canQuitWithoutLoss || displayQuitCustomizationModal) {
      return;
    }

    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.returnValue = onQuitConfirmationMessage;

      return onQuitConfirmationMessage;
    };

    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [canQuitWithoutLoss, displayQuitCustomizationModal, onQuitConfirmationMessage]);

  return (
    <div className="CustomizationHeader">
      <div className="CustomizationHeader-left">{quitElement}</div>
      <div className="CustomizationHeader-center">
        <CharacteristicsSummary showOptions={false} showProductName={false} />
      </div>
      <div className="CustomizationHeader-right">
        {/* helpElement */}
        {saveElement}
        {finalizeElement}
      </div>
      <RemainingActionsModal
        display={displayRemainingActionsModal}
        emptyPages={emptyPagesNames}
        noCroppedPictures={displayRemainingActionsModal}
        onConfirm={handleConfirmRemainingActionsModal}
        onDismiss={hideRemainingActionsModal}
        templateType={template.templateType}
      />
      <QuitCustomizationModal
        display={displayQuitCustomizationModal}
        isGuest={configuration?.isGuest}
        onClose={() => setDisplayQuitCustomizationModal(false)}
        onConfirm={handleConfirmQuitCustomizationModal}
        onDismiss={hideQuitCustomizationModal}
      />
      <ConfirmModal
        display={displayMissingPagesModal}
        onDismiss={handleDismissMissingPagesModal}
        showDismissButton={false}
      >
        <h2 className="CustomizationHeader-finalize-modale-title">
          <TranslationByType
            pageName={configuration?.pageName}
            templateType={template.templateType}
            translationBase="kustom_CustomizationHeader_missingPagesModal_title"
            pageNameChoice="plural"
          />
        </h2>
        <p className="CustomizationHeader-finalize-modale-text">
          <TranslationByType
            pageName={configuration?.pageName}
            templateType={template.templateType}
            translationBase="kustom_CustomizationHeader_missingPagesModal_text"
            pageNameChoice="plural"
          />
        </p>
      </ConfirmModal>
      <OptionChoiceModal
        configuration={configuration}
        display={displayOptionChoiceModal}
        locale={content.locale}
        onConfirm={handleConfirmOptionChoiceModal}
        onDismiss={hideOptionChoiceModal}
        template={template}
      />
    </div>
  );
};

export default CustomizationHeader;
